import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  zigbee,
  type InyUzolZigbee,
  type KvalitaSieteZigbee,
  type KvalitaUzlaZigbee,
  type MapaSieteZigbee,
  type PosudokKanala,
  type StavMostaZigbee,
  type ZigbeeZariadenie,
} from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Notice,
  PageHeader,
  SectionTitle,
  StatusDot,
} from '../components/ui.js';
import { IconChevronRight, IconPencil, IconPlus, IconRefresh } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data, popisDostupnosti, tonDostupnosti } from './common.js';
import { stavSenzora, stavoveSchopnosti } from './controls.js';

/*
 * Zigbee.
 *
 * LoxLink Zigbee nespravuje - robi to Zigbee2MQTT, ktory bezi vedla neho
 * (ADR-0010). Tato obrazovka je preto kratka: stav mosta, zoznam
 * zariadeni a JEDNA vec navyse - povolenie parovania.
 *
 * Prave parovanie by inak vyzadovalo rozhranie Zigbee2MQTT, ktore je
 * v nasadeni viazane len na slucku. Tunelovat SSH kvoli jednej ziarovke
 * je zbytocne; vsetko ostatne (premenovanie, skupiny, OTA) zostava tam.
 */

/** Ako dlho sa smie parovat a ako casto sa obrazovka obnovuje. */
const CAS_PAROVANIA_S = 240;

/** Čo znamená zdroj dostupnosti; ukazuje sa v bublinovom popise. */
const POPIS_ZDROJA: Record<string, string> = {
  most: 'Dostupnosť hlási Zigbee2MQTT',
  odhad: 'Odvodené z čerstvej správy zo zariadenia — most dostupnosť neposiela',
  ziadna: 'Zatiaľ nič: most dostupnosť neposiela a zariadenie sa neozvalo',
};
const INTERVAL_MS = 5000;

/*
 * Odpocet parovania.
 *
 * Kym parovanie bezi, obrazovka sa obnovuje kazdu sekundu - bez toho by
 * cas skakal po piatich a pouzivatel by netusil, ci este stihne
 * zariadenie uviest do parovacieho rezimu.
 */
const INTERVAL_PAROVANIA_MS = 1000;

/** `240` -> `4:00`. */
function formatujCas(sekundy: number): string {
  const minuty = Math.floor(sekundy / 60);
  const zvysok = sekundy % 60;
  return `${minuty}:${String(zvysok).padStart(2, '0')}`;
}

export function Zigbee(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const nacitanie = useApi((signal) => zigbee.prehlad(signal));
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [zapina, setZapina] = useState(false);

  // Pocas parovania sa stav meni po sekundach, inak staci pokojne tempo.
  const parovanieBezi = nacitanie.data?.bridge.permitJoin === true;
  useEffect(() => {
    const timer = setInterval(
      () => {
        nacitanie.refresh();
      },
      parovanieBezi ? INTERVAL_PAROVANIA_MS : INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [nacitanie.refresh, parovanieBezi]);

  const [zapinaDostupnost, setZapinaDostupnost] = useState(false);
  const [mapuje, setMapuje] = useState(false);
  const [chybaMapy, setChybaMapy] = useState<string | undefined>(undefined);

  /*
   * Mapovanie siete.
   *
   * Most prejde routery a spyta sa ich, koho vidia - trva to desiatky
   * sekund a odpoved pride az potom, na inu temu. Preto sa tu necaka na
   * vysledok: obrazovka sa obnovuje sama a mapa sa objavi, ked dorazi.
   */
  const zmapuj = (): void => {
    setChybaMapy(undefined);
    setMapuje(true);
    void zigbee
      .mapNetwork()
      .catch((error: unknown) => {
        setChybaMapy(error instanceof Error ? error.message : 'Sieť sa nepodarilo zmapovať.');
      })
      .finally(() => setMapuje(false));
  };

  /*
   * Zapnutie sledovania dostupnosti v moste.
   *
   * Zigbee2MQTT ho ma predvolene vypnute; bez neho sa „prestalo
   * odpovedat" zistit neda. Zmena vyzaduje jeho restart, takze chvilu
   * potrva, nez sa zase ohlasi.
   */
  const zapniDostupnost = (): void => {
    setChyba(undefined);
    setZapinaDostupnost(true);
    void zigbee
      .enableAvailability()
      .then(() => new Promise((r) => setTimeout(r, 15000)))
      .then(() => nacitanie.refresh())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Sledovanie sa nepodarilo zapnúť.');
      })
      .finally(() => setZapinaDostupnost(false));
  };

  const povolParovanie = (): void => {
    setChyba(undefined);
    setZapina(true);
    void zigbee
      .permitJoin(CAS_PAROVANIA_S)
      .then(() => nacitanie.refresh())
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Párovanie sa nepodarilo povoliť.');
      })
      .finally(() => setZapina(false));
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Zigbee"
        detail="Most Zigbee2MQTT a zariadenia, ktoré cezeň prichádzajú."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />

      <Data nacitanie={nacitanie}>
        {(data) => (
          <div className="flex flex-col gap-6">
            <Most
              stav={data.bridge}
              posudok={data.posudok}
              jeSpravca={jeSpravca}
              zapina={zapina}
              zapinaDostupnost={zapinaDostupnost}
              chyba={chyba}
              naParovanie={povolParovanie}
              naZapnutie={zapniDostupnost}
            />

            <section className="flex flex-col gap-3">
              <SectionTitle>Zariadenia ({data.devices.length})</SectionTitle>
              {data.devices.length === 0 ? (
                <EmptyState
                  title="Žiadne Zigbee zariadenie"
                  detail={
                    data.bridge.online
                      ? 'Most beží. Spáruj zariadenie tlačidlom vyššie a uveď ho do párovacieho režimu.'
                      : 'Most sa zatiaľ neohlásil. Skontroluj, či beží Zigbee2MQTT a či je kľúč ConBee pripojený.'
                  }
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.devices.map((d) => (
                    <Card as="li" key={d.id} className="flex flex-col gap-3 p-4">
                      {/*
                       * Na mobile pod sebou, na sirsej obrazovke vedla
                       * seba. Vsetko v jednom riadku bolo na telefone
                       * natlacene a nazvy sa lamali po pismenach.
                       */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate font-medium text-(--color-text)">{d.name}</span>
                          <span className="truncate text-xs text-(--color-text-subtle)">
                            {d.model ?? 'neznámy model'}
                            {d.manufacturer !== null && ` · ${d.manufacturer}`}
                          </span>
                        </div>
                        <Link
                          to={`/zariadenia/${d.id}`}
                          aria-label={`Detail zariadenia ${d.name}`}
                          className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-(--color-accent)"
                        >
                          Detail
                          <IconChevronRight className="size-4" />
                        </Link>
                      </div>

                      <Vazby zariadenie={d} jeSpravca={jeSpravca} naObnovu={nacitanie.refresh} />

                      <Firmver zariadenie={d} jeSpravca={jeSpravca} />

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <Badge tone={tonDostupnosti(d.availability)}>
                          {popisDostupnosti(d.availability)}
                        </Badge>
                        {/*
                         * Stavy senzora aj tu, nie len v zozname zariadení:
                         * pri ladení čidla je človek práve na tejto obrazovke
                         * a chce vidieť, či pohyb vôbec prišiel.
                         */}
                        {stavoveSchopnosti(d)
                          .map((name) => ({ name, stav: stavSenzora(d, name) }))
                          .map(({ name, stav }) =>
                            stav === undefined ? null : (
                              <Badge key={name} tone={stav.ton}>
                                {stav.popis}
                              </Badge>
                            ),
                          )}
                        {d.lastAction !== null && <Badge tone="accent">{d.lastAction.value}</Badge>}
                        <span
                          className="text-xs text-(--color-text-subtle)"
                          title={POPIS_ZDROJA[d.availabilitySource]}
                        >
                          {d.lastMessageAt === null
                            ? 'nič neposlalo'
                            : new Date(d.lastMessageAt).toLocaleString('sk-SK')}
                        </span>
                      </div>
                    </Card>
                  ))}
                </ul>
              )}
            </section>

            {data.otherNodes.length > 0 && (
              <IneUzly
                uzly={data.otherNodes}
                jeSpravca={jeSpravca}
                naObnovu={() => nacitanie.refresh()}
              />
            )}

            {data.linkQuality !== null && <KvalitaSpojov kvalita={data.linkQuality} />}

            <MapaSiete
              mapa={data.networkMap}
              bezi={data.networkMapRunning || mapuje}
              jeSpravca={jeSpravca}
              naMapovanie={zmapuj}
              chyba={chybaMapy ?? data.networkMapError ?? undefined}
            />
          </div>
        )}
      </Data>
    </div>
  );
}

/**
 * Uzly, ktore do modelu nepatria.
 *
 * Opakovac v sieti je a prenasa cudziu prevadzku, ale nema co hlasit ani
 * co ovladat - do Loxone teda nejde. Nechat ho uplne mimo prehladu by
 * ale vyzeralo, ze sa neparoval.
 */
function IneUzly({
  uzly,
  jeSpravca,
  naObnovu,
}: {
  readonly uzly: readonly InyUzolZigbee[];
  readonly jeSpravca: boolean;
  readonly naObnovu: () => void;
}): ReactNode {
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Rozširujú sieť ({uzly.length})</SectionTitle>
      <p className="text-xs text-(--color-text-subtle)">
        Sú v Zigbee sieti a prenášajú cudziu prevádzku, ale nemajú čo hlásiť ani čo ovládať. Do
        Loxone sa neposielajú a mapovanie im vytvoriť nejde. Názov majú len v moste — LoxLink im
        vlastný nedáva, preto sa premenúvajú tu.
      </p>
      <ul className="flex flex-col gap-2">
        {uzly.map((u) => (
          <Card
            as="li"
            key={u.ieeeAddress}
            className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-(--color-text)">{u.friendlyName}</span>
              <span className="truncate text-xs text-(--color-text-subtle)">
                {u.model ?? 'neznámy model'}
                {u.manufacturer !== undefined && ` · ${u.manufacturer}`}
                {u.type !== undefined && ` · ${u.type}`}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={u.unsupported ? 'warn' : 'neutral'}>
                {u.unsupported ? 'most ho nepozná' : 'rozširuje sieť'}
              </Badge>
              {jeSpravca && <PremenovanieUzla uzol={u} naObnovu={naObnovu} />}
            </div>
          </Card>
        ))}
      </ul>
    </section>
  );
}

/**
 * Premenovanie uzla, ktory nie je zariadenim v modeli.
 *
 * Robi to most - jeho nazov je jediny, ktory taky uzol ma. Preto sa to
 * nerobi v detaile zariadenia: ziadne zariadenie neexistuje.
 */
function PremenovanieUzla({
  uzol,
  naObnovu,
}: {
  readonly uzol: InyUzolZigbee;
  readonly naObnovu: () => void;
}): ReactNode {
  const [otvorene, setOtvorene] = useState(false);
  const [meno, setMeno] = useState(uzol.friendlyName);
  const [bezi, setBezi] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);

  const uloz = (): void => {
    setChyba(undefined);
    setBezi(true);
    void zigbee
      .premenujUzol(uzol.friendlyName, meno)
      // Most si zmenu ulozi a posle novy zoznam; chvilu to trva.
      .then(() => new Promise((r) => setTimeout(r, 1500)))
      .then(() => {
        setOtvorene(false);
        naObnovu();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Uzol sa nepodarilo premenovať.');
      })
      .finally(() => setBezi(false));
  };

  if (!otvorene) {
    return (
      <Button
        variant="ghost"
        icon={<IconPencil className="size-4" />}
        onClick={() => setOtvorene(true)}
      >
        Premenovať
      </Button>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <input
        value={meno}
        maxLength={64}
        aria-label={`Nový názov pre ${uzol.friendlyName}`}
        onChange={(e) => setMeno(e.target.value)}
        className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text)"
      />
      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={uloz} disabled={bezi || meno.trim() === uzol.friendlyName}>
          {bezi ? 'Ukladám…' : 'Uložiť'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOtvorene(false);
            setMeno(uzol.friendlyName);
            setChyba(undefined);
          }}
          disabled={bezi}
        >
          Zrušiť
        </Button>
      </div>
    </div>
  );
}

/*
 * Platno sa nacitava az ked je co kreslit.
 *
 * `vis-network` ma aj so stylmi pol megabajtu; v hlavnom baliku by ho
 * stiahol kazdy, kto otvori LoxLink, hoci mapu si pozrie malokto.
 */
const PlatnoMapy = lazy(() => import('./zigbee-map.js'));

function MapaSiete({
  mapa,
  bezi,
  jeSpravca,
  naMapovanie,
  chyba,
}: {
  readonly mapa: MapaSieteZigbee | null;
  readonly bezi: boolean;
  readonly jeSpravca: boolean;
  readonly naMapovanie: () => void;
  readonly chyba: string | undefined;
}): ReactNode {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Mapa siete</SectionTitle>
        {jeSpravca && (
          <Button onClick={naMapovanie} disabled={bezi} icon={<IconRefresh className="size-4" />}>
            {bezi ? 'Mapujem…' : mapa === null ? 'Zmapovať sieť' : 'Zmapovať znova'}
          </Button>
        )}
      </div>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      {mapa === null ? (
        <Card className="p-4">
          <p className="text-sm text-(--color-text-muted)">
            {bezi
              ? 'Most sa pýta routerov, koho vidia. Trvá to desiatky sekúnd; mapa sa objaví sama.'
              : 'Zigbee nie je hviezda — koncové zariadenie hovorí s koordinátorom cez routery. Zmapovanie ukáže, ktorou cestou. Sieť to na chvíľu zaťaží, preto sa nespúšťa samo.'}
          </p>
        </Card>
      ) : (
        <Card className="flex flex-col gap-3 p-4">
          <Suspense
            fallback={
              <div className="h-96 w-full rounded-(--radius-control) border border-(--color-border) bg-(--color-surface-sunken)" />
            }
          >
            <PlatnoMapy mapa={mapa} />
          </Suspense>

          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-(--color-text-subtle)">
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rotate-45 bg-(--color-accent)" />
              koordinátor
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full bg-(--color-ok)" />
              router
            </div>
            <div className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full border border-(--color-border-strong)" />
              koncové zariadenie
            </div>
            <div>hrúbka čiary = kvalita spojenia</div>
            <div>uzly sa dajú ťahať, kolieskom sa približuje</div>
            <div>zmapované {new Date(mapa.at).toLocaleString('sk-SK')}</div>
          </dl>
        </Card>
      )}
    </section>
  );
}

/*
 * Kanal 802.15.4 a Wi-Fi.
 *
 * Posudok (ktore Wi-Fi sa prekryvaju, kde ma siet pokoj) pocita SERVER
 * a prichadza v prehlade. Tu bola chvilu druha kopia tej matematiky;
 * s prichodom obrazovky Radia by boli uz tri a ani jedna z tych
 * v prehliadaci by nemala vlastny test.
 */
function popisKanala(posudok: PosudokKanala): string {
  const { kanal, rusiaceWifi } = posudok;
  if (rusiaceWifi.length === 0) return `${kanal} · mimo hlavných kanálov Wi-Fi`;
  return `${kanal} · prekrýva sa s Wi-Fi ${rusiaceWifi.join(' a ')}`;
}

/*
 * Kvalita spojov.
 *
 * Mapa siete nesie LQI kazdej vazby, ale surove cisla sa citaju zle.
 * Podstatne je, co je na hrane - a to je zaroven zaklad na porovnanie
 * pred presunom koordinatora a po nom (ziadost zadavatela 2026-10-09).
 */
function KvalitaSpojov({ kvalita }: { readonly kvalita: KvalitaSieteZigbee }): ReactNode {
  const ton = (k: KvalitaUzlaZigbee['kvalita']): 'ok' | 'warn' | 'danger' | 'neutral' => {
    if (k === 'dobra') return 'ok';
    if (k === 'slaba') return 'warn';
    if (k === 'kriticka') return 'danger';
    return 'neutral';
  };
  const popis: Record<KvalitaUzlaZigbee['kvalita'], string> = {
    dobra: 'dobrá',
    slaba: 'slabá',
    kriticka: 'kritická',
    neznama: 'bez väzby',
  };

  // Zoznam je zoradeny od najhorsieho; dobre spoje nie je co riesit.
  const naRiesenie = kvalita.uzly.filter((u) => u.kvalita === 'slaba' || u.kvalita === 'kriticka');

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Kvalita spojov</SectionTitle>

      <Card className="flex flex-col gap-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="inline-flex items-center gap-1.5">
            <StatusDot tone="ok" />
            <span className="text-(--color-text)">dobré: {kvalita.dobrych}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <StatusDot tone="warn" />
            <span className="text-(--color-text)">slabé: {kvalita.slabych}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <StatusDot tone="danger" />
            <span className="text-(--color-text)">kritické: {kvalita.kritickych}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <StatusDot tone="neutral" />
            <span className="text-(--color-text)">bez väzby: {kvalita.bezVazby}</span>
          </span>
        </div>

        <p className="text-xs text-(--color-text-muted)">
          Z poslednej mapy siete ({new Date(kvalita.at).toLocaleString('sk-SK')}). Berie sa
          najlepšia väzba zariadenia — stačí jedna dobrá cesta. „Bez väzby" nie je porucha: spiace
          zariadenia sa počas prehliadky často neozvú. Čísla sa hodia aj na porovnanie, keď
          presunieš koordinátor — zmapuj sieť pred presunom aj po ňom.
        </p>

        {naRiesenie.length === 0 ? (
          <p className="text-sm text-(--color-text-muted)">
            Žiadny slabý ani kritický spoj. Sieť má kde dýchať.
          </p>
        ) : (
          <ul className="flex flex-col">
            {naRiesenie.map((u) => (
              <li
                key={u.ieeeAddress}
                className="flex flex-wrap items-baseline justify-between gap-2 border-b border-(--color-border) py-2 last:border-b-0"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <StatusDot tone={ton(u.kvalita)} />
                  <span className="truncate text-sm text-(--color-text)">{u.friendlyName}</span>
                </span>
                <span className="text-xs text-(--color-text-muted)">
                  {popis[u.kvalita]} · LQI {u.najlepsieLqi ?? '—'}
                  {u.lqiNaKoordinator !== undefined &&
                    ` · priamo na koordinátor ${u.lqiNaKoordinator}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function Most({
  stav,
  posudok,
  jeSpravca,
  zapina,
  zapinaDostupnost,
  chyba,
  naParovanie,
  naZapnutie,
}: {
  readonly stav: StavMostaZigbee;
  readonly posudok: PosudokKanala | null;
  readonly jeSpravca: boolean;
  readonly zapina: boolean;
  readonly zapinaDostupnost: boolean;
  readonly chyba: string | undefined;
  readonly naParovanie: () => void;
  readonly naZapnutie: () => void;
}): ReactNode {
  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Most Zigbee2MQTT</SectionTitle>
        <Badge tone={stav.online ? 'ok' : 'danger'}>{stav.online ? 'Beží' : 'Neohlásil sa'}</Badge>
      </div>

      <dl>
        <DataRow label="Koordinátor">{stav.coordinator ?? 'zatiaľ neohlásený'}</DataRow>
        <DataRow label="Verzia Zigbee2MQTT">{stav.version ?? 'zatiaľ neohlásená'}</DataRow>
        <DataRow label="Priestor tém">{stav.baseTopic}</DataRow>
        <DataRow label="Kanál 802.15.4">
          {posudok === null ? 'zatiaľ neohlásený' : popisKanala(posudok)}
        </DataRow>
        {stav.panId !== undefined && (
          <DataRow label="PAN ID">
            <span className="font-mono text-xs">
              0x{stav.panId.toString(16).padStart(4, '0')}
              {stav.extendedPanId !== undefined && ` · ${stav.extendedPanId}`}
            </span>
          </DataRow>
        )}
        <DataRow label="Párovanie">
          {stav.permitJoin
            ? stav.permitJoinRemainingSeconds === null
              ? 'Prebieha'
              : `Prebieha · zostáva ${formatujCas(stav.permitJoinRemainingSeconds)}`
            : 'Vypnuté'}
        </DataRow>
      </dl>

      {!stav.online && (
        <Notice tone="warn">
          <p className="font-medium">Most sa zatiaľ neohlásil.</p>
          <p className="mt-1">
            Kým nebeží Zigbee2MQTT a nie je pripojený kľúč ConBee, zo Zigbee siete nepríde nič.
            Skontroluj ho príkazom <code>docker compose logs zigbee2mqtt</code>.
          </p>
        </Notice>
      )}

      {stav.online && stav.deviceCount > 0 && !stav.availabilityReported && (
        <Notice tone="warn">
          <p className="font-medium">Most nesleduje dostupnosť zariadení.</p>
          <p className="mt-1">
            LoxLink vie odvodiť, že zariadenie <em>odpovedá</em> — z jeho poslednej správy. Že
            <strong> prestalo</strong> odpovedať, sa z ticha odvodiť nedá: odpojená zásuvka zostane
            hlásená ako dostupná, kým odhad po pol hodine nezostarne na „neznámy". Skutočnú
            nedostupnosť vie povedať len most, ktorý zariadenia sám kontroluje.
          </p>
          {jeSpravca && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button onClick={naZapnutie} disabled={zapinaDostupnost}>
                {zapinaDostupnost ? 'Zapínam…' : 'Zapnúť sledovanie dostupnosti'}
              </Button>
              <span className="text-xs">
                Nastavenie sa uloží v moste a ten sa reštartuje — potrvá to pár desiatok sekúnd.
              </span>
            </div>
          )}
        </Notice>
      )}

      {posudok !== null && posudok.rusiaceWifi.length > 0 && (
        <Notice tone="warn">
          <p className="font-medium">
            Zigbee beží na kanáli {posudok.kanal}, ktorý sa prekrýva s Wi-Fi{' '}
            {posudok.rusiaceWifi.join(' a ')}.
          </p>
          <p className="mt-1">
            Pásmo 2,4 GHz zdieľajú Zigbee, Thread aj Wi-Fi. Mimo hlavných kanálov Wi-Fi ležia kanály{' '}
            {posudok.ticheKanaly.join(', ')} — tam má sieť pokoj. Zmena kanála je ale rušivý zásah:
            spiace zariadenia (tlačidlá, PIR) ju často nestihnú a treba ich spárovať znova, takže ju
            rob len vtedy, keď sieť naozaj zlyháva.
          </p>
        </Notice>
      )}

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

      {stav.permitJoin ? (
        <Notice tone="ok">
          <p className="font-medium">
            Párovanie beží
            {stav.permitJoinRemainingSeconds !== null &&
              ` · zostáva ${formatujCas(stav.permitJoinRemainingSeconds)}`}
            .
          </p>
          <p className="mt-1">
            Uveď zariadenie do párovacieho režimu — pri väčšine žiaroviek je to päť rýchlych vypnutí
            a zapnutí. Po spárovaní sa objaví v zozname nižšie samo.
          </p>
        </Notice>
      ) : (
        jeSpravca && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              icon={<IconPlus className="size-4" />}
              onClick={naParovanie}
              disabled={zapina || !stav.online}
            >
              {zapina ? 'Zapínam…' : `Povoliť párovanie na ${CAS_PAROVANIA_S / 60} minúty`}
            </Button>
            <span className="text-xs text-(--color-text-subtle)">
              Premenovanie, skupiny a aktualizácie firmvéru zostávajú v rozhraní Zigbee2MQTT.
            </span>
          </div>
        )
      )}
    </Card>
  );
}

/*
 * Vazby zariadenia.
 *
 * Ovladac - tlacidlo, PIR - nehlasi stav sam od seba: posiela Zigbee
 * prikaz tomu, s kym je ZVIAZANY. Bez vazby na koordinator most o
 * stlaceni ani o pohybe nikdy nepocuje a zariadenie vyzera pokazene,
 * hoci hlasi batériu. Stalo sa 2026-09-15 pri PIR E1745.
 *
 * Do rozhrania Zigbee2MQTT sa pouzivatel nedostane, takze toto je jedine
 * miesto, kde to uvidi a napravi.
 */
function Vazby({
  zariadenie,
  jeSpravca,
  naObnovu,
}: {
  readonly zariadenie: ZigbeeZariadenie;
  readonly jeSpravca: boolean;
  readonly naObnovu: () => void;
}): ReactNode {
  const [prebieha, setPrebieha] = useState(false);
  const [vysledok, setVysledok] = useState<string | undefined>(undefined);

  const naKoordinator = zariadenie.bindings.some((v) => v.target === 'koordinátor');
  const adresa = zariadenie.ieeeAddress;

  const zviaz = (): void => {
    if (adresa === undefined) return;
    setVysledok(undefined);
    setPrebieha(true);
    void zigbee
      .bind(adresa)
      .then(({ bind }) => {
        setVysledok(bind.ok ? 'Väzba vytvorená.' : (bind.error ?? 'Väzbu sa nepodarilo vytvoriť.'));
        if (bind.ok) naObnovu();
      })
      .catch((error: unknown) => {
        setVysledok(error instanceof Error ? error.message : 'Väzbu sa nepodarilo vytvoriť.');
      })
      .finally(() => setPrebieha(false));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-(--color-text-subtle)">
        <span>
          {zariadenie.bindings.length === 0
            ? 'Bez väzieb'
            : zariadenie.bindings.map((v) => `${v.cluster} → ${v.target}`).join(', ')}
        </span>
        {jeSpravca && adresa !== undefined && !naKoordinator && (
          <Button onClick={zviaz} disabled={prebieha}>
            {prebieha ? 'Viažem…' : 'Zviazať s koordinátorom'}
          </Button>
        )}
      </div>
      {!naKoordinator && (
        <p className="text-xs text-(--color-text-subtle)">
          Bez väzby na koordinátor hlási toto zariadenie len batériu. Batériové zariadenie musí byť
          pri viazaní hore — stlač na ňom tlačidlo hneď po spustení.
        </p>
      )}
      {vysledok !== undefined && <Notice tone="warn">{vysledok}</Notice>}
    </div>
  );
}

/*
 * Firmver zariadenia.
 *
 * Most sam zistuje, ci je novsia verzia, a hlasi to v stave zariadenia.
 * Doteraz to LoxLink cital a nikde neukazoval. Aktualizacia sa spusta
 * odtialto, lebo do rozhrania Zigbee2MQTT sa pouzivatel nedostane.
 *
 * Text pri tlacidle je dolezity: OTA cez Zigbee trva desiatky minut az
 * hodinu a bez upozornenia je to pasca, hlavne pri bateriovom zariadeni.
 */
function Firmver({
  zariadenie,
  jeSpravca,
}: {
  readonly zariadenie: ZigbeeZariadenie;
  readonly jeSpravca: boolean;
}): ReactNode {
  const [prebieha, setPrebieha] = useState(false);
  const [sprava, setSprava] = useState<string | undefined>(undefined);

  const firmware = zariadenie.firmware;
  const adresa = zariadenie.ieeeAddress;
  if (firmware === undefined || adresa === undefined) return null;

  const skontroluj = (): void => {
    setSprava(undefined);
    setPrebieha(true);
    void zigbee
      .checkFirmware(adresa)
      .then(({ check }) => {
        setSprava(check.ok ? 'Skontrolované.' : (check.error ?? 'Kontrola sa nepodarila.'));
      })
      .catch((error: unknown) => {
        setSprava(error instanceof Error ? error.message : 'Kontrola sa nepodarila.');
      })
      .finally(() => setPrebieha(false));
  };

  const aktualizuj = (): void => {
    setSprava(undefined);
    setPrebieha(true);
    void zigbee
      .updateFirmware(adresa)
      .then(() => {
        setSprava('Aktualizácia spustená. Beží ďalej aj keď zavrieš stránku.');
      })
      .catch((error: unknown) => {
        setSprava(error instanceof Error ? error.message : 'Aktualizáciu sa nepodarilo spustiť.');
      })
      .finally(() => setPrebieha(false));
  };

  if (firmware.state === 'updating') {
    const percenta = firmware.progress === undefined ? undefined : Math.round(firmware.progress);
    const minuty =
      firmware.remaining === undefined
        ? undefined
        : Math.max(1, Math.round(firmware.remaining / 60));
    return (
      <p className="text-xs text-(--color-accent)">
        Aktualizuje sa firmvér
        {percenta === undefined ? '' : ` — ${String(percenta)} %`}
        {minuty === undefined ? '' : `, zostáva asi ${String(minuty)} min`}. Zariadenie musí zostať
        napájané.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-(--color-text-subtle)">
        <span
          title={
            firmware.installedVersion === undefined
              ? undefined
              : `Nainštalovaná verzia ${String(firmware.installedVersion)}`
          }
        >
          {firmware.state === 'available' ? 'Firmvér: čaká novšia verzia' : 'Firmvér: aktuálny'}
        </span>
        {jeSpravca && (
          <>
            <Button onClick={skontroluj} disabled={prebieha}>
              Skontrolovať
            </Button>
            {firmware.state === 'available' && (
              <Button variant="primary" onClick={aktualizuj} disabled={prebieha}>
                Aktualizovať firmvér
              </Button>
            )}
          </>
        )}
      </div>
      {firmware.state === 'available' && (
        <p className="text-xs text-(--color-text-subtle)">
          Aktualizácia trvá desiatky minút až hodinu a zariadenie pri nej musí zostať napájané a v
          dosahu. Pri batériovom zariadení odoberá veľa energie — pri slabej batérii sa nemusí
          dokončiť.
        </p>
      )}
      {sprava !== undefined && <Notice tone="warn">{sprava}</Notice>}
    </div>
  );
}
